import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  CartridgeVariantAlternativeOwnerResponseDto,
  CartridgeVariantCurrentOwnersResponseDto,
} from '@markant-platform/shared/user/model';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { AdminMode, AdminSelected, Assignments, CartridgeOwner } from '@markant-platform/shared/organization/model';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';
import { CommonModule } from '@angular/common';

export
@Component({
  selector: 'markant-platform-service-responsibility-assign-new-admin',
  templateUrl: './service-responsibility-assign-new-admin.component.html',
  styleUrls: ['./service-responsibility-assign-new-admin.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MarkantElementsModule, TranslocoPipe, FormControlKeyErrorPipe],
})
class ServiceResponsibilityAssignNewAdminComponent implements OnChanges {
  @Input() owners: CartridgeVariantCurrentOwnersResponseDto[] = [];
  @Input() alternativeOwners: CartridgeVariantAlternativeOwnerResponseDto[] = [];
  @Input() mode: AdminMode = 'unassign';

  @Output() closed = new EventEmitter<void>();
  @Output() assignNewOwnersToCartridges = new EventEmitter<AdminSelected[]>();

  isWarningDialogOpen = false;
  isAssignNewAdminDialogOpen = false;
  isConfirmationDialogOpen = false;
  isNoAdminAvailableOpen = false;

  form: FormGroup = new FormGroup({});
  selectedAdmins: AdminSelected[] = [];
  assignments: Assignments[] = [];
  cartridges: CartridgeOwner[] = [];
  showErrors = false;
  alternativeOwnerOptions: MarkantSelectOption[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alternativeOwners']) {
      this.alternativeOwnerOptions = this.alternativeOwners.map(({ userId, firstname, lastname }) => ({
        value: userId,
        representation: `${firstname} ${lastname}`,
      }));

      if (this.alternativeOwners.length) {
        this.isWarningDialogOpen = true;
      } else {
        this.isNoAdminAvailableOpen = true;
      }
    }

    if (changes['owners']) {
      this.owners.forEach(owner => {
        owner.ownedCartridgeVariants.forEach(cartridge => {
          const cartridgeWithOwner = { ...cartridge, currentOwner: `${owner.firstname} ${owner.lastname}` };

          this.form.addControl(cartridge.id, new FormControl('', Validators.required));
          this.cartridges.push(cartridgeWithOwner);
        });
      });
    }
  }

  closeDialog(): void {
    this.isWarningDialogOpen = false;
    this.isAssignNewAdminDialogOpen = false;
    this.isNoAdminAvailableOpen = false;
    this.closed.emit();
  }

  continueToAvailableAdmins(): void {
    this.isWarningDialogOpen = false;
    this.isAssignNewAdminDialogOpen = true;
  }

  assignNewOwners(): void {
    if (this.form.invalid) {
      this.showErrors = true;
      return;
    }

    for (const field in this.form.controls) {
      const control = this.form.get(field);
      this.selectedAdmins.push({ cartridgeVariantId: field, userId: control?.value });
    }

    this.assignments = this.selectedAdmins.map(admin => {
      const newOwner = this.alternativeOwners.find(owner => owner.userId === admin.userId);

      const cartridgeName = this.owners
        .map(owner => owner.ownedCartridgeVariants.filter(cart => cart.id === admin.cartridgeVariantId)[0]?.name)
        .filter(name => name !== undefined)[0];

      return { firstname: newOwner!.firstname, lastname: newOwner!.lastname, cartridgeName: cartridgeName };
    });

    this.assignNewOwnersToCartridges.emit(this.selectedAdmins);
  }

  getCartridgeVariants(userId: string): string[] {
    return (
      this.owners
        .find(owner => owner.userId === userId)
        ?.ownedCartridgeVariants.map(cartridgeVariant => cartridgeVariant.name) || []
    );
  }
}
